import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Snackbar from '@material-ui/core/Snackbar';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';

import { appVersionActions } from './store';

// Poll a small, always-fresh build marker and compare it to the marker this tab
// loaded with. asset-manifest.json is emitted by every craco/CRA build and lives
// at the app root (respecting PUBLIC_URL, e.g. /modern/ for the release build).
const MARKER_URL = `${process.env.PUBLIC_URL || ''}/asset-manifest.json`;
const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes

// Fetch the marker with caching defeated. Any failure (offline, 404, parse) is
// swallowed and simply skips this round — never surfaced to the user.
const fetchMarker = async () => {
  try {
    const response = await fetch(`${MARKER_URL}?_=${Date.now()}`, { cache: 'no-store' });
    if (!response.ok) return null;
    return await response.text();
  } catch (e) {
    return null;
  }
};

const UpdateController = () => {
  const dispatch = useDispatch();
  const updateAvailable = useSelector((state) => state.appVersion.updateAvailable);

  const loaded = useRef(null);    // marker this tab started with (the baseline)
  const latest = useRef(null);    // most recent marker seen from the server
  const dismissed = useRef(null); // marker the user chose to ignore

  useEffect(() => {
    let active = true;

    const check = async () => {
      // Poll politely: do nothing while the tab is hidden.
      if (document.hidden) return;
      const current = await fetchMarker();
      if (!active || current == null) return;

      // First successful read establishes the baseline for this tab.
      if (loaded.current == null) {
        loaded.current = current;
        return;
      }

      latest.current = current;
      // "different", not "newer" — so rollbacks also prompt. Don't re-nag a
      // version the user already dismissed until an even newer one appears.
      if (current !== loaded.current && current !== dismissed.current) {
        dispatch(appVersionActions.setUpdateAvailable(true));
      }
    };

    check(); // establish baseline + immediate check on mount
    const timer = setInterval(check, POLL_INTERVAL);

    // Catch users returning to the tab without waiting for the next interval.
    const onVisible = () => { if (!document.hidden) check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      active = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [dispatch]);

  const handleRefresh = () => {
    // Never auto-reload — this only runs on an explicit user click. HashRouter
    // keeps the current route and the session cookie survives, so the user lands
    // back where they were, still logged in.
    window.location.reload();
  };

  const handleDismiss = () => {
    // Remember this build so the banner doesn't nag again until a newer deploy.
    dismissed.current = latest.current;
    dispatch(appVersionActions.setUpdateAvailable(false));
  };

  return (
    <Snackbar
      open={updateAvailable}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      message="A new version is available."
      action={(
        <>
          <Button color="secondary" size="small" onClick={handleRefresh}>
            Refresh
          </Button>
          <IconButton size="small" color="inherit" onClick={handleDismiss}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      )}
    />
  );
};

export default UpdateController;
